import { AggHomogenizationData } from '@/api/workOrders/types';
import { fmtCurrency } from '@/helpers';
import { Box, Divider, Grid, Typography } from '@mui/material';
import { memo } from 'react';

import { GroupStatusIcon } from '../../components/StatusIndicators';
import { STATISTIC_FIELD_LABELS } from '../constants';
import {
  isAggHomogenizationComplete,
  isAggSanitizationComplete,
} from '../utils/completionCheckers';

interface AggregatedProps {
  aggregated: AggHomogenizationData;
  elementsCount: number;
}

export const Aggregated = memo(function Aggregated(props: AggregatedProps) {
  const { aggregated, elementsCount } = props;

  return (
    <Grid id="fac-aggregated" container spacing={2} my={2}>
      <Grid item xs={12}>
        <Divider />
        <Box display="flex" alignItems="center" gap={1}>
          <GroupStatusIcon
            isComplete={isAggHomogenizationComplete(aggregated)}
          />
          <Typography my={2} fontWeight={600}>
            Homogeneização
          </Typography>
        </Box>
      </Grid>
      <Grid item xs={3}>
        <Typography variant="caption" color="text.secondary">
          {STATISTIC_FIELD_LABELS.homogenized_elements}
        </Typography>
        <Typography>{elementsCount}</Typography>
      </Grid>
      <Grid item xs={3}>
        <Typography variant="caption" color="text.secondary">
          {STATISTIC_FIELD_LABELS.homogenized_mean}
        </Typography>
        <Typography>{fmtCurrency(aggregated.homogenized_mean)}</Typography>
      </Grid>
      <Grid item xs={3}>
        <Typography variant="caption" color="text.secondary">
          {STATISTIC_FIELD_LABELS.homogenized_lower_mean}
        </Typography>
        <Typography>
          {fmtCurrency(aggregated.homogenized_lower_mean)}
        </Typography>
      </Grid>
      <Grid item xs={3}>
        <Typography variant="caption" color="text.secondary">
          {STATISTIC_FIELD_LABELS.homogenized_upper_mean}
        </Typography>
        <Typography>
          {fmtCurrency(aggregated.homogenized_upper_mean)}
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <Divider />
        <Box display="flex" alignItems="center" gap={1}>
          <GroupStatusIcon isComplete={isAggSanitizationComplete(aggregated)} />
          <Typography my={2} fontWeight={600}>
            Saneamento
          </Typography>
        </Box>
      </Grid>
      <Grid item xs={3}>
        <Typography variant="caption" color="text.secondary">
          {STATISTIC_FIELD_LABELS.sanitized_elements}
        </Typography>
        <Typography>{aggregated.sanitized_elements ?? '-'}</Typography>
      </Grid>
      <Grid item xs={3}>
        <Typography variant="caption" color="text.secondary">
          {STATISTIC_FIELD_LABELS.sanitized_mean}
        </Typography>
        <Typography>{fmtCurrency(aggregated.sanitized_mean)}</Typography>
      </Grid>
      <Grid item xs={3}>
        <Typography variant="caption" color="text.secondary">
          {STATISTIC_FIELD_LABELS.sanitized_std}
        </Typography>
        <Typography>{fmtCurrency(aggregated.sanitized_std)}</Typography>
      </Grid>
      <Grid item xs={3}>
        <Typography variant="caption" color="text.secondary">
          {STATISTIC_FIELD_LABELS.sanitized_lower_mean}
        </Typography>
        <Typography>{fmtCurrency(aggregated.sanitized_lower_mean)}</Typography>
      </Grid>
      <Grid item xs={3}>
        <Typography variant="caption" color="text.secondary">
          {STATISTIC_FIELD_LABELS.sanitized_upper_mean}
        </Typography>
        <Typography>{fmtCurrency(aggregated.sanitized_upper_mean)}</Typography>
      </Grid>
      <Grid item xs={3}>
        <Typography variant="caption" color="text.secondary">
          T de Student
        </Typography>
        <Typography>{aggregated.t_student ?? '-'}</Typography>
      </Grid>
      <Grid item xs={3}>
        <Typography variant="caption" color="text.secondary">
          {STATISTIC_FIELD_LABELS.upper_limit}
        </Typography>
        <Typography>{fmtCurrency(aggregated.upper_limit)}</Typography>
      </Grid>
      <Grid item xs={3}>
        <Typography variant="caption" color="text.secondary">
          {STATISTIC_FIELD_LABELS.lower_limit}
        </Typography>
        <Typography>{fmtCurrency(aggregated.lower_limit)}</Typography>
      </Grid>
      <Grid item xs={3}>
        <Typography variant="caption" color="text.secondary">
          Intervalo de Confiabilidade (%)
        </Typography>
        <Typography>
          {aggregated.confidence_interval != null
            ? `${aggregated.confidence_interval}`
            : '-'}
        </Typography>
      </Grid>
    </Grid>
  );
});
